import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Globe, Menu, X } from "lucide-react";
import Lenis from "lenis";
import i18n from "@/i18n";
import { lpath } from "@/lib/lang";
import { useDemoModal } from "@/components/site/DemoModal";

export const Wordmark = ({ light = false }) => (
  <span className={`font-sans text-[26px] font-extrabold lowercase tracking-tight transition-colors duration-500 ${light ? "text-white" : "text-ostelo-deep"}`}>
    ostelo<span className="text-ostelo-orange">.</span>
  </span>
);

export const Nav = ({ lang }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const L = (p = "") => lpath(lang, p);
  const openDemo = useDemoModal();

  useEffect(() => setOpen(false), [location.pathname]);

  const otherLang = lang === "fr" ? "en" : "fr";
  const switched = `/${otherLang}${location.pathname.replace(/^\/(fr|en)/, "")}`;

  // Header unique : barre bleu nuit translucide + flou, texte blanc — lisible sur tous les fonds
  const linkCls = ({ isActive }) =>
    `inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
      isActive ? "text-white" : "text-white/80 hover:text-white"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ostelo-deep/75 shadow-[0_10px_40px_rgb(12,27,71,0.18)] backdrop-blur-xl">
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link
          to={L("")}
          onClick={(e) => {
            /* Déjà sur l'accueil : le clic remonte en haut du Hero au lieu de ne rien faire */
            if (location.pathname.replace(/\/$/, "") === L("")) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          data-testid="nav-logo"
          aria-label="Ostelo"
          className="shrink-0"
        >
          <Wordmark light />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          <NavLink to={L("")} end data-testid="nav-solution" className={linkCls}>
            {t("nav.solution")}
          </NavLink>
          <NavLink to={L("/tarifs")} data-testid="nav-tarifs" className={linkCls}>
            {t("nav.tarifs")}
          </NavLink>
          <NavLink to={L("/a-propos")} data-testid="nav-apropos" className={linkCls}>
            {t("nav.apropos")}
          </NavLink>
          <NavLink to={L("/contact")} data-testid="nav-contact" className={linkCls}>
            {t("nav.contact")}
          </NavLink>
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <Link
            to={switched}
            data-testid="lang-switcher"
            aria-label="FR / EN"
            title={otherLang.toUpperCase()}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors duration-300 hover:bg-white/10"
          >
            <Globe size={16} />
          </Link>
          <button
            type="button"
            onClick={openDemo}
            data-testid="nav-demo-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-ostelo-orange px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-ostelo-deep shadow-[0_8px_25px_rgb(249,157,47,0.35)] transition-[background-color,transform] duration-300 hover:scale-[0.97] hover:bg-[#FFAC47]"
          >
            {t("nav.demo")}
            <ChevronRight size={13} />
          </button>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
        >
          {open ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-ostelo-deep/5 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1 px-6 py-6">
              <Link to={L("")} className="rounded-lg px-3 py-3 text-base font-medium text-ostelo-ink hover:bg-ostelo-sky">{t("nav.solution")}</Link>
              <Link to={L("/tarifs")} className="rounded-lg px-3 py-3 text-base font-medium text-ostelo-ink hover:bg-ostelo-sky">{t("nav.tarifs")}</Link>
              <Link to={L("/a-propos")} className="rounded-lg px-3 py-3 text-base font-medium text-ostelo-ink hover:bg-ostelo-sky">{t("nav.apropos")}</Link>
              <Link to={L("/contact")} className="rounded-lg px-3 py-3 text-base font-medium text-ostelo-ink hover:bg-ostelo-sky">{t("nav.contact")}</Link>
              <div className="mt-4 flex items-center justify-between gap-4">
                <Link to={switched} data-testid="lang-switcher-mobile" className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] text-ostelo-ink/60">
                  <Globe size={15} />
                  <span className={lang === "fr" ? "text-ostelo-deep" : ""}>FR</span>
                  <span className="text-ostelo-orange">/</span>
                  <span className={lang === "en" ? "text-ostelo-deep" : ""}>EN</span>
                </Link>
                <button type="button" onClick={() => { setOpen(false); openDemo(); }} data-testid="mobile-demo-cta" className="rounded-full bg-ostelo-orange px-5 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-ostelo-deep">
                  {t("nav.demo")}
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default function Layout() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const valid = lang === "fr" || lang === "en";

  useEffect(() => {
    if (!valid) {
      // Ne retire un préfixe que si c'est un segment complet suivi de "/" ou de la fin
      // (ex: /de/tarifs -> /fr/tarifs). Sinon le chemin est conservé tel quel :
      // /admin donne /fr/admin (404 propre) et non plus /frmin.
      const target = `/fr${location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "")}`;
      navigate(target, { replace: true });
      return;
    }
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  }, [lang, valid, navigate, location.pathname]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!valid) return null;

  return (
    <div className="min-h-screen bg-white">
      <Nav lang={lang} />
      <main className="pt-[76px]">
        <Outlet />
      </main>
      <Footer lang={lang} />
    </div>
  );
}

export const Footer = ({ lang }) => {
  const { t } = useTranslation();
  const L = (p = "") => lpath(lang, p);
  const openDemo = useDemoModal();
  const year = new Date().getFullYear();

  const cols = [
    {
      title: t("footer.col_company"),
      links: [
        { to: L("/tarifs"), label: t("nav.tarifs") },
        { to: L("/a-propos"), label: t("nav.apropos") },
        { to: L("/contact"), label: t("nav.contact") },
        { demo: true, label: t("nav.demo") },
      ],
    },
    {
      title: t("footer.col_legal"),
      links: [
        { to: L("/mentions-legales"), label: t("footer.legal_mentions") },
        { to: L("/politique-confidentialite"), label: t("footer.legal_privacy") },
        { to: L("/cookies"), label: t("footer.legal_cookies") },
      ],
    },
  ];

  return (
    <footer className="bg-ostelo-deep text-white" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid gap-12 md:grid-cols-[1.6fr_1fr_1fr]">
          <div>
            <Wordmark light />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/65">{t("footer.tagline")}</p>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.2em] text-white/45">{t("footer.contact_title")}</p>
              <a href={`mailto:${t("footer.contact_email")}`} data-testid="footer-email" className="mt-2 inline-block text-sm font-semibold text-ostelo-orange transition-colors duration-300 hover:text-white">
                {t("footer.contact_email")}
              </a>
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.demo ? (
                      <button type="button" onClick={openDemo} className="text-sm text-white/70 transition-colors duration-300 hover:text-ostelo-orange">
                        {l.label}
                      </button>
                    ) : (
                      <Link to={l.to} className="text-sm text-white/70 transition-colors duration-300 hover:text-ostelo-orange">
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/45 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} Ostelo. {t("footer.rights")}</p>
          <p className="inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-ostelo-orange" />
            {t("footer.made")}
          </p>
        </div>
      </div>
    </footer>
  );
};
